// Phase 17 — Unified Campaign API client.
// Phase 17.8 — added blob-download helpers for PDF + DOCX (window.open
// strips the Authorization header in a new tab, which was the regression).
import { api as client } from "@/api/client";

const BASE = "/api/campaigns";

function triggerBlobDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const unifiedCampaigns = {
    create: (body) => client.post(BASE, body).then((r) => r.data),
    list: (params = {}) => client.get(BASE, { params }).then((r) => r.data),
    get: (id) => client.get(`${BASE}/${id}`).then((r) => r.data),
    cancel: (id) => client.post(`${BASE}/${id}/cancel`).then((r) => r.data),
    // Phase 18.A — Clone Campaign: server-side deep-copy of source
    // config blocks, auto-runs as a fresh queued campaign.
    clone: (id, name = null) => client.post(
        `${BASE}/${id}/clone`,
        name ? { name } : {},
    ).then((r) => r.data),
    // Phase 17.9d — Finalize Now: terminal-state transition that
    // preserves any partial data the worker has already written.
    finalize: (id) => client.post(
        `${BASE}/${id}/finalize`, { confirm: true },
    ).then((r) => r.data),
    remove: (id) => client.delete(`${BASE}/${id}`).then((r) => r.status),
    reportUrl: (id) => `${BASE}/${id}/report.pdf`,
    sampleTrajectory: (id, sampleIdx) =>
        client.get(`${BASE}/${id}/samples/${sampleIdx}/trajectory`).then((r) => r.data),
    downloadPdf: async (id, filename = `campaign_${id}.pdf`) => {
        const r = await client.get(`${BASE}/${id}/report.pdf`, { responseType: "blob" });
        triggerBlobDownload(r.data, filename);
        return r.data;
    },
    downloadDocx: async (id, filename = `campaign_${id}.docx`) => {
        const r = await client.get(`${BASE}/${id}/report.docx`, { responseType: "blob" });
        triggerBlobDownload(r.data, filename);
        return r.data;
    },
    // Phase 20 CP5.b — multi-agent (Phase 19) 5-band SoW report.
    // Backed by GET /api/campaigns/{id}/report.multiagent.{pdf,docx}
    // (see backend/routers/unified_campaigns.py). Streams the
    // WeasyPrint render off the campaign's persisted per-sample
    // fidelity (CP4.5) — no engine re-run, evaluator gets the
    // exact evidence the campaign produced.
    downloadMultiagentPdf: async (
        id, filename = `campaign_${id}_multiagent.pdf`,
    ) => {
        const r = await client.get(
            `${BASE}/${id}/report.multiagent.pdf`, { responseType: "blob" },
        );
        triggerBlobDownload(r.data, filename);
        return r.data;
    },
    downloadMultiagentDocx: async (
        id, filename = `campaign_${id}_multiagent.docx`,
    ) => {
        const r = await client.get(
            `${BASE}/${id}/report.multiagent.docx`, { responseType: "blob" },
        );
        triggerBlobDownload(r.data, filename);
        return r.data;
    },
    // Phase 19 demo follow-on — WS-E procurement-audit POST. Admin-only.
    // Returns the response body verbatim:
    //   {path, public_path, sha256, page_count, source_date_epoch,
    //    generated_at, cached}
    // The frontend opens ``public_path`` in a new tab as the download
    // link (mirror under ``/app/frontend/public/exports/...``).
    generateProcurementAuditPdf: async (id) => {
        const r = await client.post(
            `${BASE}/${id}/procurement-audit-pdf`, {},
        );
        return r.data;
    },
};

export default unifiedCampaigns;
